// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Training prices, in US dollars per 1,000,000 tokens, checked on the date in
 * TABLE_CHECKED.
 *
 * Two design rules, both of which exist because a price table in a static site
 * is wrong eventually and the only question is whether it is wrong LOUDLY:
 *
 *  - Every entry carries the date it was read and the page it was read from,
 *    and the interface shows the date beside the figure.
 *  - Every price is EDITABLE in the interface. A user who knows the number has
 *    moved can correct it and get a right answer today, instead of waiting for
 *    a deploy.
 *
 * The one thing this file must never do is present a rate as current without
 * saying when it was checked.
 */

export interface PriceEntry {
  readonly id: string;
  readonly provider: string;
  readonly label: string;
  /** US dollars per 1,000,000 training tokens. */
  readonly perMillion: number;
  /** Minimum charged per job, in dollars, where the provider states one. */
  readonly minimumUsd?: number;
  /** Does this provider bill validation-file tokens too? They differ, and it matters. */
  readonly billsValidation: boolean;
  readonly source: string;
  readonly note?: string;
}

/**
 * OpenAI is winding down fine-tuning.
 *
 * Its own pricing page carries the banner "OpenAI is winding down the
 * fine-tuning platform. The platform is no longer accessible to new users", and
 * the deprecations page dates the schedule: 7 May 2026, no new organisations;
 * 2 July 2026, no organisation that has not run inference on a fine-tuned model
 * in the previous 60 days; 6 January 2027, nobody at all.
 *
 * The prices below are still correct for the organisations that can still use
 * them, so they ship — but they ship with this notice attached, because
 * presenting them like any other live option would send somebody to build
 * against a platform they cannot join.
 */
export const OPENAI_WINDDOWN =
  'OpenAI is winding down fine-tuning. New organisations have been unable to start since 7 May 2026, and no new fine-tuning jobs can be created after 6 January 2027. Inference on models already fine-tuned continues until the base model is deprecated.';

export const PRICES: readonly PriceEntry[] = [
  /* ── still open to new users ───────────────────────────────────────────── */
  {
    id: 'together-lora-small',
    provider: 'Together AI',
    label: 'LoRA SFT, smaller models',
    perMillion: 0.48,
    minimumUsd: 4,
    billsValidation: true,
    source: 'together.ai/pricing, fine-tuning table',
  },
  {
    id: 'together-lora-large',
    provider: 'Together AI',
    label: 'LoRA SFT, largest models',
    perMillion: 2.9,
    minimumUsd: 4,
    billsValidation: true,
    source: 'together.ai/pricing, fine-tuning table',
  },
  {
    id: 'together-full-small',
    provider: 'Together AI',
    label: 'Full SFT, smaller models',
    perMillion: 1.2,
    minimumUsd: 4,
    billsValidation: true,
    source: 'together.ai/pricing, fine-tuning table',
  },
  {
    id: 'together-full-large',
    provider: 'Together AI',
    label: 'Full SFT, largest models',
    perMillion: 7.25,
    minimumUsd: 4,
    billsValidation: true,
    source: 'together.ai/pricing, fine-tuning table',
  },
  {
    id: 'fireworks-16b',
    provider: 'Fireworks',
    label: 'LoRA SFT, up to 16B',
    perMillion: 0.5,
    billsValidation: false,
    source: 'fireworks.ai/pricing, tuning table',
  },
  {
    id: 'fireworks-80b',
    provider: 'Fireworks',
    label: 'LoRA SFT, 16.1B–80B',
    perMillion: 3.0,
    billsValidation: false,
    source: 'fireworks.ai/pricing, tuning table',
  },
  {
    id: 'fireworks-300b',
    provider: 'Fireworks',
    label: 'LoRA SFT, 80B–300B',
    perMillion: 6.0,
    billsValidation: false,
    source: 'fireworks.ai/pricing, tuning table',
  },

  /* ── legacy: correct, but the platform is closing ──────────────────────── */
  {
    id: 'openai-gpt-4.1',
    provider: 'OpenAI',
    label: 'gpt-4.1-2025-04-14',
    perMillion: 25.0,
    billsValidation: false,
    source: 'developers.openai.com/api/docs/pricing',
    note: OPENAI_WINDDOWN,
  },
  {
    id: 'openai-gpt-4.1-mini',
    provider: 'OpenAI',
    label: 'gpt-4.1-mini-2025-04-14',
    perMillion: 5.0,
    billsValidation: false,
    source: 'developers.openai.com/api/docs/pricing',
    note: OPENAI_WINDDOWN,
  },
  {
    id: 'openai-gpt-4.1-nano',
    provider: 'OpenAI',
    label: 'gpt-4.1-nano-2025-04-14',
    perMillion: 1.5,
    billsValidation: false,
    source: 'developers.openai.com/api/docs/pricing',
    note: OPENAI_WINDDOWN,
  },
  {
    id: 'openai-gpt-4o',
    provider: 'OpenAI',
    label: 'gpt-4o-2024-08-06',
    perMillion: 25.0,
    billsValidation: false,
    source: 'developers.openai.com/api/docs/pricing',
    note: OPENAI_WINDDOWN,
  },
  {
    id: 'openai-gpt-4o-mini',
    provider: 'OpenAI',
    label: 'gpt-4o-mini-2024-07-18',
    perMillion: 3.0,
    billsValidation: false,
    source: 'developers.openai.com/api/docs/pricing',
    note: OPENAI_WINDDOWN,
  },
  {
    id: 'openai-gpt-3.5',
    provider: 'OpenAI',
    label: 'gpt-3.5-turbo',
    perMillion: 8.0,
    billsValidation: false,
    source: 'developers.openai.com/api/docs/pricing',
    note: OPENAI_WINDDOWN,
  },
];

export function priceById(id: string): PriceEntry {
  const found = PRICES.find((p) => p.id === id);
  if (!found) throw new Error(`unknown price ${id}`);
  return found;
}
